/**
 * Composable function to run a batch mutation based on Promise.allSettled
 * @param options {Object} - The options for the batch mutation
 * @param options.batchSize {number} - The batch size
 * @param options.stopOnError {boolean} - Whether to stop on error
 * @param options.shouldStop {Function} - The function to check if the batch should stop
 * @param options.onBatchComplete {Function} - The function to call when the batch is complete
 * @param options.onProgress {Function} - The function to call when the progress is updated
 * @param options.onComplete {Function} - The function to call when the batch is complete
 */
export default function useBatchMutation(options = {}) {
  const {
    batchSize = 10,
    stopOnError = false,
    shouldStop,
    onBatchComplete,
    onProgress,
    onComplete,
  } = options

  /**
   * Run the batch mutation
   * @param tasks {Function[]} - The tasks to run
   * @returns {Promise<{results: any[], error: Error | null}>} - The results and error
   */
  async function run(tasks = []) {
    const total = tasks.length
    const results = []
    let error = null

    for (let i = 0; i < total; i += batchSize) {
      const batchIndex = Math.floor(i / batchSize)

      const batch = tasks
        .slice(i, i + batchSize)
        .map(fn => fn())

      // eslint-disable-next-line no-await-in-loop
      const settled = await Promise.allSettled(batch)

      results.push(...settled)

      // progress callback
      if (onProgress) {
        const percent = Math.round((results.length / total) * 100)
        onProgress(percent, results.length, total)
      }

      if (onBatchComplete) {
        onBatchComplete(settled, batchIndex)
      }

      // stop logic
      const rejected = settled.filter(r => r.status === 'rejected')

      if (rejected.length) {
        if (stopOnError) {
          error = rejected[0].reason
          break
        }

        if (shouldStop) {
          const mustStop = rejected.some(r => shouldStop(r.reason))
          if (mustStop) {
            error = rejected[0].reason
            break
          }
        }
      }
    }

    if (onComplete) {
      onComplete(results, error)
    }

    return {
      results,
      error,
    }
  }

  return { run }
}
